import type { FlowSetting, ProjectFlowSettings } from "@/api/flowSettings";

import {
  alwaysTracks,
  priorityTrackOf,
  settingOf,
  wayBack,
  type SlotDraft,
  type StagedChange,
} from "./flowSettings";

export type TrackRow = { readonly track: string; readonly required: boolean };

/** The panel rows in run order: required tracks in slot order, then the rest by ascending priority (registration order breaks ties, no priority comes last). */
export function trackRows(
  state: ProjectFlowSettings,
  slots: readonly SlotDraft[],
  priorityOf: (track: string) => number | null,
): TrackRow[] {
  const required = alwaysTracks(slots);
  const pool = [
    ...state.selection.alwaysTracks,
    ...state.selection.automaticTracks,
  ];
  const optional = pool
    .filter((track, index) => pool.indexOf(track) === index)
    .filter((track) => !required.includes(track))
    .map((track, index) => ({ track, index, priority: priorityOf(track) }))
    .sort(
      (left, right) =>
        (left.priority ?? Infinity) - (right.priority ?? Infinity) ||
        left.index - right.index,
    );
  return [
    ...required.map((track) => ({ track, required: true })),
    ...optional.map(({ track }) => ({ track, required: false })),
  ];
}

export function moveRequired(
  slots: readonly SlotDraft[],
  track: string,
  to: number,
): SlotDraft[] {
  const order = alwaysTracks(slots);
  const from = order.indexOf(track);
  if (from === -1) {
    return [...slots];
  }
  order.splice(from, 1);
  order.splice(Math.max(0, Math.min(to, order.length)), 0, track);
  let next = 0;
  return slots.map((slot) =>
    slot.kind === "always" && slot.track !== null
      ? { kind: "always", track: order[next++] ?? slot.track }
      : slot,
  );
}

/** Frees a required track's slot behind the remaining required ones, leaving every other slot in its saved order. */
export function releaseRequired(
  slots: readonly SlotDraft[],
  track: string,
): SlotDraft[] {
  const held = slots.findIndex(
    (slot) => slot.kind === "always" && slot.track === track,
  );
  if (held === -1) {
    return [...slots];
  }
  const rest = slots.filter((_, index) => index !== held);
  const lastRequired = rest.reduce(
    (last, slot, index) => (slot.kind === "always" ? index : last),
    -1,
  );
  rest.splice(lastRequired + 1, 0, { kind: "automatic", track: null });
  return rest;
}

/** Deals the numbers the tracks already hold out again in the new order, so few change; a tie breaks upward. */
export function reprioritise(
  order: readonly string[],
  current: ReadonlyMap<string, number | null>,
): Map<string, number> {
  const known = order
    .flatMap((track) => {
      const value = current.get(track);
      return value === null || value === undefined ? [] : [value];
    })
    .sort((left, right) => left - right);
  const next = new Map<string, number>();
  let previous = -1;
  order.forEach((track, index) => {
    const value = Math.max(known[index] ?? previous + 1, previous + 1);
    previous = value;
    next.set(track, value);
  });
  return next;
}

const names = (list: readonly string[]): string =>
  list.length < 2
    ? (list[0] ?? "")
    : `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;

export function summarySentence(
  requiredNames: readonly string[],
  limit: number | null,
): string | null {
  if (limit === null) {
    return null;
  }
  const more = Math.max(limit - requiredNames.length, 0);
  if (requiredNames.length === 0) {
    return limit === 1
      ? "1 track per interview, chosen by classification."
      : `Up to ${limit} tracks per interview, all chosen by classification.`;
  }
  return more === 0
    ? `${names(requiredNames)} for everyone, and nothing more by classification.`
    : `${names(requiredNames)} for everyone, then up to ${more} more by classification.`;
}

/** The loader refuses both selection shapes, so a legacy project's replaced keys are removed in the same request as its slot list. */
export function legacyRemovals(state: ProjectFlowSettings): StagedChange[] {
  if (state.selection.shape !== "legacy") {
    return [];
  }
  const replaced: [FlowSetting, string][] = [
    ["defaultTracks", "the default track list"],
    ["maxDynamicTracks", "the separate dynamic limit"],
  ];
  return replaced.flatMap(([setting, subject]) => {
    const view = settingOf(state, setting);
    return view !== null && wayBack(view).remove
      ? [{ setting, track: null, op: "delete" as const, subject }]
      : [];
  });
}

const squash = (text: string): string => text.toLowerCase().replace(/[^a-z0-9]+/g, "");

export function speaksFor(name: string, track: string): boolean {
  const wanted = squash(track);
  return wanted !== "" && squash(name).startsWith(wanted);
}

type ResetTarget = {
  readonly setting: FlowSetting;
  readonly track: string | null;
  /** What the confirmation names. Never an engine key. */
  readonly subject: string;
};

/** The replaced keys are included on purpose: restoring the slot list alone leaves neither selection shape declared. */
export function resetTargets(
  state: ProjectFlowSettings,
  nameOfTrack: (track: string) => string,
): ResetTarget[] {
  const targets: ResetTarget[] = [];
  const add = (
    setting: FlowSetting,
    subject: string,
    track: string | null = null,
  ) => {
    const view =
      track === null
        ? settingOf(state, setting)
        : state.trackPriorityEntries.find((entry) => priorityTrackOf(entry) === track);
    if (view !== null && view !== undefined && wayBack(view).restore) {
      targets.push({ setting, track, subject });
    }
  };
  add("dynamicTrackSlots", "the required tracks and tracks per interview");
  add("defaultTracks", "the default track list");
  add("maxDynamicTracks", "the separate dynamic limit");
  add("trackPriority", "the whole fallback order");
  for (const entry of state.trackPriorityEntries) {
    const track = priorityTrackOf(entry);
    if (track !== null) {
      add("trackPriority", `the fallback order of ${nameOfTrack(track)}`, track);
    }
  }
  return targets;
}
